/* eslint-disable */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  Constants,
  NavigationNodeId,
  USER_ROLES,
} from '@pwc/pwc-deals-web-common';
import {
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { Icon, Checkbox } from '@pwc/pwc-deals-ui';
import TagList from 'features/analyses/common/TagList/TagList';
import VersionStatus from 'common/components/Pills/VersionStatus';
import featureFlags from 'common/helpers/featureFlags';
import { DraftPillClientView } from 'common/components/Pills/DraftPill';
import AvatarWithMenu from 'common/components/AvatarWithMenu';
import GroupsPermissions from '../GroupsPermissions/GroupsPermissions';
import AnalysisMenu from '../AnalysisMenu/AnalysisMenu';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import {
  getNavigationLink,
  geKFAtNavigationLink,
  formatDate,
} from '../../utils/groupsUtilities';
import { getProject } from '../../../project/projectSlice';
import { useFormatDateI18n } from '../../../../common/hooks/useFomatDateI18n';
import './AnalysisItemDraggable.scss';
import { useKFATitleI18n } from '../../../../common/hooks/useKFATitleI18n';

const AnalysisItemDraggable = ({
  item,
  index,
  number,
  selectedIDs,
  groupId,
  projectId,
  handleSelect,
  handleDelete,
  handleMove,
  dragInProgress,
  permissions,
  enableDragAndDrop,
  isKfa,
  showConnectedKfa = true,
}) => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const {
    contentThreadId,
    isSelected,
    dateModified,
    modifiedBy,
    versionNumber,
    versionStatus,
    sortOrder,
    kfa,
    tags,
    title,
    subType,
    type,
  } = item;

  const [mouseDown, setMouseDown] = useState(false);
  const [isMouseOutTheCheckbox, setIsMouseOutTheCheckbox] = useState(true);
  const { navigation = [] } = useSelector(getProject);
  const formattedDateModifiedI18n = useFormatDateI18n(dateModified);
  const { isClientUser, isPwcReadOnlyRole, hasPermission } = permissions;
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  let analysisItemClass = isKfa ? 'analysis-item-kfa' : 'analysis-item-custom';
  const { t } = useTranslation();

  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);
  const KFATitleI18n = useKFATitleI18n(KFATitle);

  if (isClientUser) {
    analysisItemClass = isKfa
      ? 'analysis-item-kfa-client-view'
      : 'analysis-item-custom-client-view';
  } else if (isPwcReadOnlyRole) {
    analysisItemClass = isKfa
      ? 'analysis-item-kfa-ready-only-view'
      : 'analysis-item-custom-ready-only-view';
  }

  const handleDeleteAnalysis = () => {
    document.body.click();
    handleDelete(contentThreadId);
  };

  let handleMoveAnalysis = undefined;

  if (!isKfa) {
    handleMoveAnalysis = () => {
      document.body.click();
      handleMove(contentThreadId, sortOrder);
    };
  }

  const getItemStyle = (draggableStyle, isDragging) => ({
    userSelect: 'none',
    cursor: isDragging ? '-webkit-grab' : '-webkit-grab',
    ...draggableStyle,
    ...(isDragging && { pointerEvents: 'auto' }),
  });

  const getConnectedKfaLink = () => {
    const hidden = navigation.find(
      (nav) => nav.id === NavigationNodeId.KEY_FOCUS_AREAS
    )?.isHidden;

    if (hidden || !showConnectedKfa) {
      return;
    }

    return kfa ? (
      <div className="kfa">
        <Icon name="hypothesis" width="18" />
        <Link
          className="kfa__title"
          to={`${geKFAtNavigationLink(projectId, kfa.contentThreadId)}`}
        >
          {kfa.title}
        </Link>
      </div>
    ) : (
      <div className="kfa" data-testid="no-kfa">
        <Icon name="hypothesis" width="18" />
        <span className="kfa__no-kfa-title">
          {t('noKeyFocusAreaConnected', {
            KFATitle: KFATitleI18n,
          })}
        </span>
      </div>
    );
  };

  return (
    <Draggable
      key={String(contentThreadId)}
      draggableId={String(contentThreadId)}
      index={index}
      isDragDisabled={!enableDragAndDrop}
    >
      {(provided, snapshot) => {
        const { isDragging } = snapshot;
        const dragProps = hasPermission ? provided.dragHandleProps : {};
        return (
          <div
            draggable={!isClientUser && !isPwcReadOnlyRole}
            onMouseDown={() =>
              setMouseDown(!isClientUser && !isPwcReadOnlyRole)
            }
            onMouseUp={() => setMouseDown(false)}
            onMouseMove={() => {
              if (mouseDown && isMouseOutTheCheckbox) {
                handleSelect(contentThreadId, true, groupId);
                setMouseDown(false);
              }
            }}
            onMouseEnter={() => setIsMouseOutTheCheckbox(false)}
            onMouseLeave={() => setIsMouseOutTheCheckbox(true)}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...dragProps}
            className={classNames(
              analysisItemClass,
              (isSelected || isDragging) && `${analysisItemClass}--selected`,
              dragInProgress &&
                selectedIDs.includes(contentThreadId) &&
                !isDragging &&
                `${analysisItemClass}--hidden`,
              isDragging && `${analysisItemClass}--dragging`
            )}
            style={
              enableDragAndDrop
                ? getItemStyle(provided.draggableProps.style, isDragging)
                : null
            }
          >
            <div className={`${analysisItemClass}__container`}>
              <div className={`${analysisItemClass}__drag`}>
                <GroupsPermissions projectId={projectId}>
                  <Icon
                    name="arrow-up-down"
                    width="20"
                    className={classNames(isDragging && 'icon-orange')}
                  />
                  {(!selectedIDs.includes(contentThreadId) || !isDragging) && (
                    <Checkbox
                      dataTestId="select-for-drag-checkbox"
                      id={`select-for-drag-checkbox-${contentThreadId}`}
                      checked={isSelected}
                      onChange={(e) => {
                        handleSelect(
                          contentThreadId,
                          e.target.checked,
                          groupId
                        );
                      }}
                      disabled={!hasPermission || !enableDragAndDrop}
                    />
                  )}
                </GroupsPermissions>

                {!(isDragging && selectedIDs.includes(contentThreadId)) && (
                  <div className={`${analysisItemClass}__dummy-sort`}>
                    {number}
                  </div>
                )}
                <GroupsPermissions projectId={projectId}>
                  {isDragging && (
                    <div className={`${analysisItemClass}__drag-count`}>
                      {selectedIDs.length || 1}
                    </div>
                  )}
                </GroupsPermissions>
              </div>
              <div className={`${analysisItemClass}__main`}>
                <div className={`${analysisItemClass}__titular`}>
                  <h4>
                    <Link
                      to={`${getNavigationLink(
                        projectId,
                        contentThreadId,
                        subType,
                        type,
                        isPwcUser
                      )}`}
                    >
                      {title}
                    </Link>
                  </h4>

                  {tags && tags.length > 0 && (
                    <TagList
                      tags={tags}
                      testId="analysis-tag-list-draggable"
                      iconSize={18}
                    />
                  )}

                  {getConnectedKfaLink()}
                </div>

                {!isThirdPartyMember && (
                  <div className={`${analysisItemClass}__modified`}>
                    <h4>
                      {versionStatus === Constants.VERSION_STATUS.SHARED.value
                        ? t('lastShared')
                        : t('lastUpdated')}
                    </h4>
                    <section>{formattedDateModifiedI18n}</section>
                    {isClientUser ? null : <section>{modifiedBy}</section>}
                  </div>
                )}
              </div>
            </div>

            <div className={`${analysisItemClass}__details`}>
              {!isThirdPartyMember && (
                <div className={`${analysisItemClass}__versions`}>
                  {isClientUser ? (
                    <DraftPillClientView />
                  ) : (
                    <VersionStatus
                      content={{ versionStatus, versionNumber }}
                      allowDraftPill
                      hideTextVersion={featureFlags.isVersioningPhase1Enabled}
                    />
                  )}
                </div>
              )}

              <div className={`${analysisItemClass}__menu`}>
                <GroupsPermissions projectId={projectId}>
                  <AvatarWithMenu contentThreadId={contentThreadId}>
                    <AnalysisMenu
                      projectId={projectId}
                      handleDelete={handleDeleteAnalysis}
                      handleMove={handleMoveAnalysis}
                      analysisId={contentThreadId}
                      subType={subType}
                      type={type}
                      enableDragAndDrop={enableDragAndDrop}
                    />
                  </AvatarWithMenu>
                </GroupsPermissions>
              </div>
            </div>
          </div>
        );
      }}
    </Draggable>
  );
};

export default AnalysisItemDraggable;
